// Feature -> license reverse lookup.
// Given a feature name, list every SKU / add-on path that grants it, cheapest first.
import { parseCell } from "./matrix";

const TYPE_ORDER = { included: 0, suite: 1, addon: 2, standalone: 3 };

export const TYPE_LABELS = {
  included: "Included",
  suite: "Suite step-up",
  addon: "Add-on",
  standalone: "Standalone",
};

/** Unique feature names with the sections they appear in. */
export const buildFeatureNames = (rows) => {
  const map = new Map();
  rows.forEach((r, i) => {
    if (r.type !== "feature") return;
    const key = r.name.toLowerCase();
    const entry = map.get(key) || { name: r.name, key, count: 0, sections: new Set(), indexes: [] };
    entry.count += 1;
    entry.sections.add(r.sectionKey);
    entry.indexes.push(i);
    map.set(key, entry);
  });
  return [...map.values()]
    .map((e) => ({ ...e, sections: [...e.sections] }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const sectionTitles = (rows) => {
  const out = {};
  rows.forEach((r) => {
    if (r.type === "section") out[r.sectionKey] = r.title;
  });
  return out;
};

/** Names of the ancestor rows (lower indent) above a feature row, outermost first. */
const parentChain = (rows, index) => {
  const chain = [];
  let indent = rows[index].indent || 0;
  for (let j = index - 1; j >= 0 && indent > 0; j -= 1) {
    const r = rows[j];
    if (r.type !== "feature") break;
    const ri = r.indent || 0;
    if (ri < indent) {
      chain.unshift(r.name);
      indent = ri;
    }
  }
  return chain;
};

/** For a "⊡" cell, find the add-on row above that grants it for this plan. */
const findAddonParent = (rows, index, planId) => {
  let indent = rows[index].indent || 0;
  for (let j = index - 1; j >= 0 && indent > 0; j -= 1) {
    const r = rows[j];
    if (r.type !== "feature") break;
    const ri = r.indent || 0;
    if (ri < indent) {
      const kind = parseCell(r.cells?.[planId] || "").kind;
      if (kind === "addon") return { row: r, via: "addon" };
      if (kind === "check" || kind === "text") return { row: r, via: "direct" };
      indent = ri;
    }
  }
  return null;
};

/** For a "Δ" cell, the standalone product is the nearest ancestor that is itself sold separately. */
const findSeparateParent = (rows, index) => {
  let indent = rows[index].indent || 0;
  let found = null;
  for (let j = index - 1; j >= 0 && indent > 0; j -= 1) {
    const r = rows[j];
    if (r.type !== "feature") break;
    const ri = r.indent || 0;
    if (ri < indent) {
      const sep = Object.values(r.cells || {}).some((v) => parseCell(v).kind === "separate");
      if (sep) found = r;
      indent = ri;
    }
  }
  return found;
};

export const computeLookup = ({ rows, plans, addons = [], name, priceMode = "teams" }) => {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  const matchIdx = [];
  rows.forEach((r, i) => {
    if (r.type === "feature" && r.name.toLowerCase() === key) matchIdx.push(i);
  });
  if (!matchIdx.length) return null;

  const enabled = plans.filter((p) => p.enabled);
  const planById = Object.fromEntries(plans.map((p) => [p.id, p]));
  const titles = sectionTitles(rows);
  const addonInfo = (n) => addons.find((a) => a.name.toLowerCase() === String(n).toLowerCase()) || null;
  const priceOf = (plan) => (plan?.price ? plan.price[priceMode] : null);
  const sum = (values) => (values.some((v) => v === null || v === undefined) ? null : values.reduce((a, b) => a + b, 0));

  // Plans that include the feature directly (used to prune dominated add-on paths).
  const directGrants = new Set();
  matchIdx.forEach((i) => {
    const row = rows[i];
    enabled.forEach((plan) => {
      const kind = parseCell(row.cells?.[plan.id] || "").kind;
      if ((kind === "check" || kind === "text") && !plan.addon) directGrants.add(plan.id);
    });
  });

  const paths = new Map();
  const add = (p) => {
    const existing = paths.get(p.key);
    if (!existing) {
      paths.set(p.key, { ...p, contexts: [...p.contexts] });
      return;
    }
    if (p.level && !existing.level) existing.level = p.level;
    if (p.viaParent && !existing.viaParent) existing.viaParent = p.viaParent;
    p.contexts.forEach((c) => {
      if (!existing.contexts.some((x) => x.rowId === c.rowId)) existing.contexts.push(c);
    });
  };

  const planPart = (plan) => ({ id: plan.id, label: plan.short, name: plan.name, price: priceOf(plan), kind: "plan", estimated: !!plan.estimated, groupId: plan.group });
  const suitePart = (plan) => ({ id: plan.id, label: plan.short, name: plan.name, price: plan.price ? plan.price.teams : null, kind: "suite", estimated: !!plan.estimated, groupId: plan.group });
  const addonPart = (n) => {
    const info = addonInfo(n);
    return { id: `addon:${n}`, label: n, name: n, price: info ? info.price : null, kind: "addon", note: info?.note || "", known: !!info, estimated: !!info?.estimated };
  };

  matchIdx.forEach((i) => {
    const row = rows[i];
    const ctx = { rowId: row.id, section: titles[row.sectionKey] || row.sectionKey, parents: parentChain(rows, i), url: row.url };
    let standalone = false;

    enabled.forEach((plan) => {
      const cell = parseCell(row.cells?.[plan.id] || "");
      const level = cell.kind === "text" ? cell.text.join(" ") : null;

      if (cell.kind === "check" || cell.kind === "text") {
        if (plan.addon) {
          const baseIds = plan.requires?.length
            ? plan.requires
            : enabled.filter((p) => p.group === plan.group && !p.addon).map((p) => p.id);
          baseIds
            .map((id) => planById[id])
            .filter((b) => b && b.enabled)
            .forEach((base) => {
              if (directGrants.has(base.id)) return;
              const parts = [planPart(base), suitePart(plan)];
              add({
                key: `suite:${base.id}+${plan.id}`,
                type: "suite",
                title: `${base.name} + ${plan.name}`,
                parts,
                total: sum(parts.map((x) => x.price)),
                level,
                groupId: base.group,
                contexts: [ctx],
              });
            });
        } else {
          const parts = [planPart(plan)];
          add({
            key: `inc:${plan.id}`,
            type: "included",
            title: plan.name,
            parts,
            total: sum(parts.map((x) => x.price)),
            level,
            groupId: plan.group,
            contexts: [ctx],
          });
        }
        return;
      }

      if (cell.kind === "addon") {
        if (plan.addon) return;
        const parts = [planPart(plan), addonPart(row.name)];
        add({
          key: `addon:${plan.id}+${row.name.toLowerCase()}`,
          type: "addon",
          title: `${plan.name} + ${row.name}`,
          addon: row.name,
          parts,
          total: sum(parts.map((x) => x.price)),
          level: null,
          groupId: plan.group,
          contexts: [ctx],
        });
        return;
      }

      if (cell.kind === "included") {
        if (plan.addon) return;
        const parent = findAddonParent(rows, i, plan.id);
        if (!parent) return;
        if (parent.via === "direct") {
          const parts = [planPart(plan)];
          add({
            key: `inc:${plan.id}`,
            type: "included",
            title: plan.name,
            parts,
            total: sum(parts.map((x) => x.price)),
            level: null,
            viaParent: parent.row.name,
            groupId: plan.group,
            contexts: [ctx],
          });
          return;
        }
        const parts = [planPart(plan), addonPart(parent.row.name)];
        add({
          key: `addon:${plan.id}+${parent.row.name.toLowerCase()}`,
          type: "addon",
          title: `${plan.name} + ${parent.row.name}`,
          addon: parent.row.name,
          parts,
          total: sum(parts.map((x) => x.price)),
          level: null,
          groupId: plan.group,
          contexts: [ctx],
        });
        return;
      }

      if (cell.kind === "separate") {
        standalone = true;
        // The product may also be bundled in an add-on listed above (e.g. Entra Suite -> Entra Internet Access)
        if (!plan.addon) {
          const parent = findAddonParent(rows, i, plan.id);
          if (parent && parent.via === "addon") {
            const parts = [planPart(plan), addonPart(parent.row.name)];
            add({
              key: `addon:${plan.id}+${parent.row.name.toLowerCase()}`,
              type: "addon",
              title: `${plan.name} + ${parent.row.name}`,
              addon: parent.row.name,
              parts,
              total: sum(parts.map((x) => x.price)),
              level: null,
              groupId: plan.group,
              contexts: [ctx],
            });
          }
        }
      }
    });

    if (standalone) {
      const product = findSeparateParent(rows, i) || row;
      const part = addonPart(product.name);
      add({
        key: `standalone:${product.name.toLowerCase()}`,
        type: "standalone",
        title: `${product.name} (standalone)`,
        addon: product.name,
        parts: [part],
        total: part.price,
        level: null,
        groupId: null,
        contexts: [ctx],
      });
    }
  });

  const list = [...paths.values()]
    .filter((p) => {
      // prune add-on / suite paths whose base plan already includes the feature
      if (p.type === "included" || p.type === "standalone") return true;
      return !directGrants.has(p.parts[0].id);
    })
    .map((p) => ({ ...p, estimated: p.parts.some((x) => x.estimated) }));

  list.sort((a, b) => {
    const an = a.total === null || a.total === undefined;
    const bn = b.total === null || b.total === undefined;
    if (an !== bn) return an ? 1 : -1;
    if (!an && a.total !== b.total) return a.total - b.total;
    if (TYPE_ORDER[a.type] !== TYPE_ORDER[b.type]) return TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
    return a.title.localeCompare(b.title);
  });

  const contexts = [];
  matchIdx.forEach((i) => {
    const row = rows[i];
    contexts.push({ rowId: row.id, section: titles[row.sectionKey] || row.sectionKey, parents: parentChain(rows, i), url: row.url });
  });

  return {
    name: rows[matchIdx[0]].name,
    url: rows.map((r, i) => (matchIdx.includes(i) ? r.url : "")).find(Boolean) || "",
    contexts,
    paths: list,
    directCount: list.filter((p) => p.type === "included").length,
    cheapest: list.find((p) => p.total !== null && p.total !== undefined) || null,
  };
};

const sumParts = (parts) => (parts.some((x) => x.price === null || x.price === undefined) ? null : parts.reduce((a, x) => a + x.price, 0));

/**
 * Multi-feature compare: the cheapest single bundle (base plan +/- suite +/- add-ons) covering every pinned feature.
 * Reuses the per-feature paths from computeLookup and combines them per base plan.
 */
export const computeCompare = ({ rows, plans, addons = [], names, priceMode = "teams" }) => {
  const features = names.map((n) => computeLookup({ rows, plans, addons, name: n, priceMode })).filter(Boolean);
  if (!features.length) return null;
  const bases = plans.filter((p) => p.enabled && !p.addon && p.price);
  const combos = new Map();

  bases.forEach((base) => {
    const basePart = features.flatMap((f) => f.paths).find((p) => p.parts[0].id === base.id)?.parts[0] || {
      id: base.id, label: base.short, name: base.name, price: base.price[priceMode], kind: "plan", estimated: !!base.estimated, groupId: base.group,
    };
    // options per feature: the extra parts (beyond the base) that grant it with this base
    const options = features.map((f) =>
      f.paths
        .filter((p) => p.type === "standalone" || p.parts[0].id === base.id)
        .map((p) => ({ path: p, extras: p.type === "standalone" ? p.parts : p.parts.slice(1) }))
    );
    if (options.some((o) => o.length === 0)) return;

    const walk = (i, chosen, extras) => {
      if (i === options.length) {
        const parts = [basePart, ...extras.values()];
        const key = parts.map((x) => x.id).sort().join("|");
        if (!combos.has(key)) {
          combos.set(key, {
            key,
            base,
            parts,
            total: sumParts(parts),
            estimated: parts.some((x) => x.estimated),
            coverage: chosen.map((c) => ({ name: c.feature.name, via: c.extras.length ? c.extras.map((x) => x.label).join(" + ") : base.short, path: c.path })),
            groupId: base.group,
          });
        }
        return;
      }
      options[i].forEach((opt) => {
        const next = new Map(extras);
        opt.extras.forEach((x) => next.set(x.id, x));
        walk(i + 1, [...chosen, { feature: features[i], extras: opt.extras, path: opt.path }], next);
      });
    };
    walk(0, [], new Map());
  });

  let list = [...combos.values()];
  list.sort((a, b) => {
    const an = a.total === null;
    const bn = b.total === null;
    if (an !== bn) return an ? 1 : -1;
    if (!an && a.total !== b.total) return a.total - b.total;
    if (a.parts.length !== b.parts.length) return a.parts.length - b.parts.length;
    return a.base.name.localeCompare(b.base.name);
  });
  // drop bundles that are a strict superset of a cheaper (or equal) bundle
  list = list.filter((c, idx) => {
    const ids = new Set(c.parts.map((x) => x.id));
    return !list.some(
      (o, j) => j < idx && o.parts.length < c.parts.length && o.parts.every((x) => ids.has(x.id)) && (o.total ?? Infinity) <= (c.total ?? Infinity)
    );
  });

  return {
    names: features.map((f) => f.name),
    missing: names.filter((n) => !features.some((f) => f.name.toLowerCase() === n.toLowerCase())),
    features,
    combos: list,
    cheapest: list.find((c) => c.total !== null) || null,
  };
};
